import i18n from '~/components/i18n'
import { RouteTypes } from '~/routes/constants'

export const getTableColumnHeaders = () => [
  {
    name: i18n.t('Billing month'),
    key: 'billingMonth',
    type: 'date',
    format: 'MMM YYYY'
  },
  {
    name: i18n.t('Supp. no.'),
    key: 'supplierNo',
    sorted: 'asc'
  },
  {
    name: i18n.t('Supplier'),
    key: 'companyName'
  },
  {
    name: i18n.t('Total amount (in kg)'),
    key: 'amountInKg',
    type: 'no-unit-number',
    decimalScale: 0
  },
  {
    name: i18n.t('Milk price net (in €)'),
    key: 'netTotal',
    type: 'no-unit-number'
  },
  {
    name: i18n.t('Milk price brutto (in €)'),
    key: 'grossTotal',
    type: 'no-unit-number'
  },
  {
    name: i18n.t('Verified?'),
    key: 'hasErrors',
    type: 'invoice-errors'
  },
  {
    name: i18n.t('Payout amount (in €)'),
    key: 'total',
    type: 'no-unit-number'
  },
  {
    name: i18n.t('Actions')
  }
]

export const actions = [
  {
    popupMessage: i18n.t('Preview'),
    type: 'icon',
    name: 'eye',
    size: 'large',
    actionType: 'preview'
  },
  {
    popupMessage: i18n.t('Download milk bill'),
    type: 'icon',
    name: 'cloud download',
    size: 'large',
    actionType: 'download'
  },
  {
    popupMessage: i18n.t('Adjust quality rating'),
    type: 'link',
    name: 'icon-results',
    size: 'large',
    link: RouteTypes.MILK_MONEY_INVOICE_EDIT
  },
  {
    popupMessage: i18n.t('Recalculate and create MGA'),
    type: 'icon',
    name: 'refresh',
    size: 'large',
    actionType: 'recalculate'
  }
]

export const milkPaymentStatus = {
  defaultStatus: 0,
  generatingStatus: 1,
  errorGeneratingStatus: 2,
  verificationStatus: 3,
  preclosingStatus: 4,
  closedStatus: 5
}

export const getSteps = () => [
  {
    title: i18n.t('Start process'),
    titleAfterFinished: i18n.t('Process started successfully'),
    description: i18n.t('Please click \'Generate your milk bill\' button'),
    statusValue: milkPaymentStatus.defaultStatus
  },
  {
    title: i18n.t('Create milk payment statements'),
    titleAfterFinished: i18n.t('MGA created successfully'),
    description: i18n.t('Please be patient while milk cash statements are generated for farmers. This process step may take a few minutes'),
    statusValue: milkPaymentStatus.generatingStatus
  },
  {
    title: i18n.t('Verify MGA'),
    titleAfterFinished: i18n.t('MGA successfully verified'),
    description: i18n.t('Please check the individual milk payment statements including the printout before continuing to create the SEPA XML.'),
    statusValue: milkPaymentStatus.verificationStatus
  },
  {
    title: i18n.t('SEPA XML'),
    titleAfterFinished: i18n.t('SEPA XML generated successfully'),
    description: i18n.t('Check and import the SEPA XML into your banking program and then close the billing month.'),
    statusValue: milkPaymentStatus.preclosingStatus
  },
  {
    title: i18n.t('Close billing month'),
    titleAfterFinished: i18n.t('MGA successfully completed'),
    description: i18n.t(''),
    statusValue: milkPaymentStatus.closedStatus
  }
]

export const milkTestFields = [
  {
    key: 'date',
    label: i18n.t('Date'),
    type: 'date-ribbon'
  },
  {
    key: 'fat',
    label: i18n.t('Fat (in %)'),
    valueType: 'number',
    requiredMessage: i18n.t('Please enter fat'),
    min: {
      value: 1,
      message: i18n.t('Min. value fat is 1')
    },
    max: {
      value: 99.99,
      message: i18n.t('Max value of fat is 99,99')
    },
    allowDecimals: 2,
    type: 'input'
  },
  {
    key: 'protein',
    label: i18n.t('Protein (in %)'),
    valueType: 'number',
    requiredMessage: i18n.t('Please enter protein'),
    min: {
      value: 1,
      message: i18n.t('Min. value protein is 1')
    },
    max: {
      value: 100,
      message: i18n.t('Max value of protein is 99,99')
    },
    allowDecimals: 2,
    type: 'input'
  },
  {
    key: 'germCount',
    label: i18n.t('Germ count (1000/ml)'),
    valueType: 'number',
    min: {
      value: 0,
      message: i18n.t('Min. value germ count is 0')
    },
    type: 'input'
  },
  {
    key: 'cellCount',
    label: i18n.t('Cell count (1000/ml)'),
    valueType: 'number',
    min: {
      value: 0,
      message: i18n.t('Min. value cell count is 0')
    },
    type: 'input'
  },
  {
    key: 'inhibitor',
    label: i18n.t('Inhibitor'),
    placeholder: i18n.t('Select inhibitor'),
    requiredMessage: i18n.t('Please select inhibitor'),
    type: 'select',
    optionKey: 'inhibitors'
  },
  {
    key: 'freezingPoint',
    label: i18n.t('Freezing point (in °C)'),
    valueType: 'number',
    suffix: ' °C',
    allowDecimals: 3,
    type: 'input'
  },
  {
    key: 'urea',
    label: i18n.t('Urea (mg/dl)'),
    valueType: 'number',
    min: {
      value: 0,
      message: i18n.t('Min. value urea is 0')
    },
    type: 'input'
  }
]

export const jsonArray = {
  'months': [
    {
      'key': '2024-05-01',
      'label': 'Mai 2024'
    },
    {
      'key': '2024-04-01',
      'label': 'April 2024'
    },
    {
      'key': '2024-03-01',
      'label': 'M\u00e4rz 2024'
    },
    {
      'key': '2024-02-01',
      'label': 'Februar 2024'
    },
    {
      'key': '2024-01-01',
      'label': 'Januar 2024'
    },
    {
      'key': '2023-12-01',
      'label': 'Dezember 2023'
    },
    {
      'key': '2023-11-01',
      'label': 'November 2023'
    },
    {
      'key': '2023-10-01',
      'label': 'Oktober 2023'
    },
    {
      'key': '2023-09-01',
      'label': 'September 2023'
    },
    {
      'key': '2023-08-01',
      'label': 'August 2023'
    },
    {
      'key': '2023-07-01',
      'label': 'Juli 2023'
    },
    {
      'key': '2023-06-01',
      'label': 'Juni 2023'
    }
  ]
}